import { gql } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import * as React from "react";
import { createContext, lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import AppLoader from "./components/AppLoader";
import createApolloClient from "./createApolloClient";
import { setTranslations } from "./utils";
import { clearStore, getCache, getInitCacheTimestamp, INDEXED_DB_TABLES, initCacheTimestamp, setCache, setCacheMultiple, } from "./utils/indexedDB";

const FrontSection = lazy(() => import("./components/front/FrontSection"));
const AdminSection = lazy(() => import("./components/admin/AdminSection"));

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 1 day

const INIT_QUERY = gql`
    {
        categories {
            id
            name
            slug
		        description
            parent
            level
            priority
            image
            companyCount
            featuredCompanyIds
            enabled
        }

        tags {
            id
            name
            companies
            demands
        }

        cities {
            id
            name
            slug
            description
		        isRegion
		        isDistrict
		        regionId
		        districtId
            image
            companyCount
		        noindex
        }
    }
`;

const USER_QUERY = gql`
    {
        me {
            username
            role
        }
    }
`;

const client = createApolloClient();

export const AppContext = createContext();

export default function App() {
	const mapViewPortRef = useRef([48.427727237228446, 19.017169081076858]);

	const [state, setState] = useState({
		error: null,
		loading: true,
		user: null,
		tags: [],
		categories: [],
		categoryTree: [],
		categoriesById: {},
		regions: [],
		cities: [],
	});

	const onLogout = useCallback((e) => {
		e.preventDefault();
		localStorage.removeItem("token");
		Object.values(INDEXED_DB_TABLES).forEach((store) => {
			clearStore(store);
		});
		setState((prev) => ({ ...prev, user: null }));
		window.location.href = "/";
	}, []);

	useEffect(() => {
		const now = Date.now();
		const cacheTimestamp = getInitCacheTimestamp();

		async function fetchData() {
			try {
				const { hostname } = window.location;
				const domain = hostname.includes("localhost")
					? "sk"
					: hostname.split(".").pop();

				let translations;

				try {
					const cached = await getCache(INDEXED_DB_TABLES.translations);
					const translationEntry = cached?.find((t) => t.id === domain);

					if (
						translationEntry &&
						cacheTimestamp &&
						now - cacheTimestamp < CACHE_DURATION
					) {
						translations = translationEntry.data;
					}
				} catch (e) {
					console.error("Error reading translations from cache", e);
				}

				if (!translations) {
					translations = await fetch(
						"/translations/" + domain + ".json?v4",
					).then((r) => r.json());
					try {
						await setCache(INDEXED_DB_TABLES.translations, {
							id: domain,
							data: translations,
						});
					} catch (e) {
						console.error("Error saving translations to cache", e);
					}
				}

				setTranslations(translations);

				let categories, cities, tags;

				const [cachedCats, cachedCities, cachedTags] = await Promise.all([
					getCache(INDEXED_DB_TABLES.categories),
					getCache(INDEXED_DB_TABLES.cities),
					getCache(INDEXED_DB_TABLES.tags),
				]);

				if (
					cachedCats?.length &&
					cacheTimestamp &&
					now - cacheTimestamp < CACHE_DURATION
				) {
					categories = cachedCats;
				}
				if (
					cachedCities?.length &&
					cacheTimestamp &&
					now - cacheTimestamp < CACHE_DURATION
				) {
					cities = cachedCities.sort((a, b) => a.name.localeCompare(b.name));
				}
				if (
					cachedTags?.length &&
					cacheTimestamp &&
					now - cacheTimestamp < CACHE_DURATION
				) {
					tags = cachedTags;
				}

				if (!categories || !cities || !tags) {
					const response = await client.query({ query: INIT_QUERY });
					categories = response.data.categories;
					cities = response.data.cities;
					tags = response.data.tags;

					initCacheTimestamp();
					await setCacheMultiple(INDEXED_DB_TABLES.categories, categories);
					await setCacheMultiple(INDEXED_DB_TABLES.cities, cities);
					await setCacheMultiple(INDEXED_DB_TABLES.tags, tags);
				}

				let me = null;
				if (localStorage.getItem("token")) {
					const response = await client.query({ query: USER_QUERY });
					me = response.data.me;
				}
				const categoryTree = [];
				const categoriesById = {};

				categories.forEach((cat) => {
					categoriesById[cat.id] = cat;
					categoriesById[cat.id].children = [];
					if (cat.parent && categoriesById[cat.parent]) {
						categoriesById[cat.parent].children.push(cat);
					} else if (!cat.parent) {
						categoryTree.push(categoriesById[cat.id]);
					}
				});

				const citiesById = {};
				const regions = [];
				const districts = [];

				cities.forEach((city) => {
					citiesById[city.id] = city;

					if (city.isRegion) {
						regions.push(city);
					}

					if (city.isDistrict) {
						districts.push(city);
					}
				});

				setState((prev) => ({
					...prev,
					user: me,
					tags,
					categories,
					categoriesById,
					categoryTree,
					cities,
					citiesById,
					regions,
					districts,
					loading: false,
				}));
			} catch (err) {
				setState((prev) => ({ ...prev, loading: false, error: err }));
			}
		}

		fetchData();
	}, []);

	if (state.loading) {
		return <AppLoader />;
	}

	if (state.error) {
		return <h1>{state.error.message}</h1>;
	}

	return (
		<HelmetProvider>
			<BrowserRouter>
				<Helmet>
					<title>UTOO.sk - Firmy a služby na 3 kliky</title>
					<meta
						name="description"
						content="UTOO.sk - nájdite najlepšie firmy a služby na 3 kliky"
					/>
				</Helmet>
				<AppContext.Provider
					value={{
						state,
						client,
						onLogout,
						mapViewPort: mapViewPortRef.current,
					}}
				>
					<ApolloProvider client={client}>
						<Suspense fallback={<AppLoader />}>
							<Routes>
								<Route path="/admin/*" element={<AdminSection />} />
								<Route path="/*" element={<FrontSection />} />
							</Routes>
						</Suspense>
					</ApolloProvider>
				</AppContext.Provider>
			</BrowserRouter>
		</HelmetProvider>
	);
}
